import type { UserFeedbackPrototype } from '@racemap/sdk/schema/userFeedback';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { Flex, FloatButton, Input, Modal, message } from 'antd';
import { useState } from 'react';
import { useCurrentUser } from 'src/lib/customHooks';
import { UAParser } from 'ua-parser-js';
import { UserLabel } from './BasicComponents/UserLabel';
import { IconCommentDots } from './Icon';

const { TextArea } = Input;
const apiClient = RacemapAPIClient.fromWindowLocation();

export const UserFeedback = () => {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const currentUser = useCurrentUser();
  const userAgent = new UAParser().getResult();
  const isInIframe = window.self !== window.top;
  const referrer = document.referrer || '';

  // Don't show feedback button if in an iframe
  if (isInIframe && referrer.includes('https://go.racemap.com')) return null;

  // Don't show feedback button if the user is not logged in
  if (currentUser === null) return null;

  const handleSend = async () => {
    if (!content.trim()) {
      message.warning('Please enter some feedback.');
      return;
    }

    setLoading(true);
    try {
      const feedback: UserFeedbackPrototype = {
        content,
        source: 'frontend',
        systemInfo: {
          browser: `${userAgent.browser.name} ${userAgent.browser.version}`,
          os: `${userAgent.os.name} ${userAgent.os.version}`,
          engine: `${userAgent.engine.name} ${userAgent.engine.version}`,
          device: userAgent.device.type || 'desktop', // can be 'mobile', 'tablet', etc.
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date(),
        },
      };

      await apiClient.createUserFeedback({ feedback });
      message.success('Feedback sent. Thank you!');
      setContent('');
      setVisible(false);
    } catch (err) {
      console.error(err);
      message.error('Failed to send feedback.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <FloatButton
        type="primary"
        icon={<IconCommentDots />}
        onClick={() => setVisible(true)}
        // Align the button with the cookies button
        style={{ right: 10, bottom: 66, padding: 3, height: 46, width: 46 }}
        tooltip="Send feedback"
      />
      <Modal
        title={
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              <Flex gap={4}>
                Hey <UserLabel userId={currentUser?.id} />. How can we help?
              </Flex>
            </div>
            <div style={{ fontSize: '13px', color: '#888' }}>
              Tell us where you struggled or got blocked — no detail is too small.
            </div>
          </div>
        }
        open={visible}
        onCancel={() => setVisible(false)}
        onOk={handleSend}
        okText="Send"
        confirmLoading={loading}
      >
        <TextArea rows={8} value={content} onChange={(e) => setContent(e.target.value)} autoFocus />
      </Modal>
    </>
  );
};
